import {RoleGroups} from '@/util/constants'

// NetSuite customlist1177 internal ids for wholesale tiers 1-4 (9=Tier 1, 10=Tier 2, 11=Tier 3, 12=Tier 4).
// Note: id 8 is Wholesale International (tier 5) and is intentionally excluded.
const ELIGIBLE_UCG_CATEGORIES = [9, 10, 11, 12]
// Title fallbacks for methods NetSuite hasn't tagged with a Pre-Settlement term id yet.
const PRE_SETTLEMENT_TITLE_HINTS = [
  'advanced pre-settlement',
  'advanced presettlement',
  'pre-settlement advance',
  'presettlement advance',
  'psp',
]

// Normalizes SSM-configured PRE_SETTLEMENT_TERM_IDS (array or comma string) to a Set.
export const toPreSettlementTermIdSet = (termIds) => {
  if (termIds instanceof Set) return termIds
  const list = Array.isArray(termIds) ? termIds : String(termIds || '').split(',')
  return new Set(list.map((id) => String(id).trim()).filter(Boolean))
}

const activationEnabledFor = (company = {}) =>
  company.preSettlementEnabled === true

export const isEligibleForPreSettlement = (company = {}) => {
  if (company.preSettlementEligible != null) return company.preSettlementEligible
  return ELIGIBLE_UCG_CATEGORIES.includes(Number(company.ucgCategory)) &&
    Number(company.creditLimit) > 0
}

// Identity is driven by the SSM-configured term ids (matched against the method's
// netsuite_internalId); title hints only cover methods NetSuite hasn't tagged yet.
export const isPreSettlementPaymentMethod = (paymentMethod, termIds) => {
  if (!paymentMethod) return false
  if (toPreSettlementTermIdSet(termIds).has(String(paymentMethod.netsuite_internalId))) return true
  const title = String(paymentMethod.title || '').toLowerCase()
  return PRE_SETTLEMENT_TITLE_HINTS.some((hint) => title.includes(hint))
}

// Acceptance reuses the APS agreement-on-file stamp.
export const hasAcceptedPreSettlementTerms = (company = {}) =>
  Boolean(company.preSettlementAgreementDate)

export const canUsePreSettlement = (company = {}) => {
  if (company.preSettlementCanUse != null) return company.preSettlementCanUse
  return activationEnabledFor(company) &&
    isEligibleForPreSettlement(company) &&
    hasAcceptedPreSettlementTerms(company)
}

// Resources terms page: activation on + eligible (enrollment still needs principal/owner).
export const canAccessPreSettlementTermsPage = (company = {}) =>
  activationEnabledFor(company) && isEligibleForPreSettlement(company)

export const requiresPreSettlementTerms = (company = {}, paymentMethod, termIds) =>
  activationEnabledFor(company) &&
  isPreSettlementPaymentMethod(paymentMethod, termIds) &&
  !canUsePreSettlement(company)

// Show Pre-Settlement methods once eligible + activation is on so Trade Ticket can
// send unsigned owners to Resources terms on their first Pre-Settlement trade.
export const filterPreSettlementPaymentMethods = (paymentMethods, company = {}, termIds) => {
  if (isEligibleForPreSettlement(company) && activationEnabledFor(company)) {
    return paymentMethods
  }
  return paymentMethods.filter((paymentMethod) =>
    !isPreSettlementPaymentMethod(paymentMethod, termIds)
  )
}

export const isPrincipalOwnerForCompany = (userProfile, companyId) => {
  const roles = userProfile?.companyPermissions?.[companyId]?.roles || []
  return roles.some((entry) =>
    RoleGroups.PRINCIPAL_OWNER_ROLE_IDS.includes(String(entry.role?.netsuite_internalId))
  )
}

// Eligible unsigned principal/owner with activation enabled.
export const canEnrollInPreSettlementTerms = (company = {}, userProfile) =>
  Boolean(company.id) &&
  activationEnabledFor(company) &&
  isEligibleForPreSettlement(company) &&
  !hasAcceptedPreSettlementTerms(company) &&
  isPrincipalOwnerForCompany(userProfile, company.id)

// Mirrors NetSuite COC_DOR_TERMS_MAPPINGS (UCG_lib_priorityHelper / payment.js TERMS).
const COC_TO_DOR_NETSUITE_IDS = Object.freeze({
  '28': '13', // COC ACH Debit → DOR ACH
  '17': '13', // COC ACH Payment → DOR ACH Payment (same NS id as DOR ACH)
  '7': '4',   // COC Check By Mail → DOR Check
  '32': '4',  // COC E-Check → DOR Check
  '22': '23', // COC Real Time → DOR Real Time
  '19': '14', // COC Wire → DOR Wire
  '18': '4',  // COC Express Check → DOR Check
  '34': '35', // COC ACH Cash Disbursement → DOR Cash Disbursement
  '40': '42', // COC Trade → DOR Trade
  '41': '42', // Prepay/APS Trade (not in NetSuite COC→DOR map; keep for opt-out)
})

const isDorPaymentMethod = (paymentMethod) =>
  String(paymentMethod?.title || '').toLowerCase().includes('dor')

export const titleModality = (title) => {
  const normalized = String(title || '').toLowerCase()
  if (normalized.includes('wire')) return 'wire'
  if (normalized.includes('ach')) return 'ach'
  return null
}

export const resolveDorPaymentMethod = (selectedPaymentMethod, paymentMethods = []) => {
  if (!selectedPaymentMethod || !paymentMethods.length) return null

  const mappedId = COC_TO_DOR_NETSUITE_IDS[String(selectedPaymentMethod.netsuite_internalId)]
  if (mappedId) {
    const mapped = paymentMethods.find(
      (method) => String(method.netsuite_internalId) === mappedId
    )
    if (mapped) return mapped
  }

  const dorMethods = paymentMethods.filter(isDorPaymentMethod)
  if (!dorMethods.length) return null

  const modality = titleModality(selectedPaymentMethod.title)
  if (!modality) return null

  return dorMethods.find((method) =>
    String(method.title || '').toLowerCase().includes(modality)
  ) || null
}
